import { normalizeLocale, type AppLocale } from './locales'
import type { RateUnit } from '@/core/display-settings'

export const displaySettingsMessages = {
  'zh-CN': {
    experienceSettingsTitle: "经验设置",
    rateUnitLabel: "显示单位",
    rateUnitScope: "全站经验、金币、图表与悬浮窗同步。",
    etaModeLabel: "升级预估",
    realtime: "实时速度",
    average: "平均速度",
    etaScope: "同步控制面板与悬浮窗。",
    immediate: "即时生效",
    done: "完成",
    etaHelp: "实时跟随近期效率，数据不足时使用平均速度；平均取当前有效统计区间。",
    helpLabel: "了解{setting}",
    etaRealtime: "按实时速度估算",
    etaAverage: "按平均速度估算",
    etaFallback: "实时速度暂不可用，当前按平均速度估算",
    etaUnavailable: "数据不足，暂无法估算",
    trendDescription: "近 {window} 秒：{p0} {unit}\n全程平均：{p1} {unit}\n差值：{p2} {unit}\n样本时间：{p3}\n偏差达到 4% 时显示，回落至 2% 后延迟 1.5 秒隐藏，并排除识别延迟造成的偏差。窗口边界和跨级经验可能采用估算。",
    unit: '单位', unitHint: '全站速率的显示单位，不影响统计和排名', hour: '小时', minute: '分钟',
    exp: '经验', mesos: '金币', model: '文字识别模型', standard: '标准模型', lightweight: '轻量模型', standardBenefit: '识别更准确', lightweightBenefit: '负载更低，识别精度下降',
    modelHint: '推荐使用标准模型。轻量模型可减少资源占用、降低设备负载，但识别精度较低，更容易出现误读或漏读。', modelHintAction: '了解模型差异',
    recordingLocked: '请先结束当前记录再切换模型', busyLocked: '请等待当前操作完成后再切换模型', switching: '正在切换模型…',
  },
  'zh-TW': {
    experienceSettingsTitle: "經驗設定",
    rateUnitLabel: "顯示單位",
    rateUnitScope: "全站經驗、楓幣、圖表與懸浮視窗同步。",
    etaModeLabel: "升級預估",
    realtime: "即時速度",
    average: "平均速度",
    etaScope: "同步控制面板與懸浮視窗。",
    immediate: "即時生效",
    done: "完成",
    etaHelp: "即時依近期效率估算，資料不足時使用平均速度；平均取目前有效統計區間。",
    helpLabel: "了解{setting}",
    etaRealtime: "依即時速度估算",
    etaAverage: "依平均速度估算",
    etaFallback: "即時速度暫不可用，目前依平均速度估算",
    etaUnavailable: "資料不足，暫時無法估算",
    trendDescription: "近 {window} 秒：{p0} {unit}\n全程平均：{p1} {unit}\n差值：{p2} {unit}\n樣本時間：{p3}\n偏差達到 4% 時顯示，回落至 2% 後延遲 1.5 秒隱藏，並排除識別延遲造成的偏差。視窗邊界和跨級經驗可能採用估算。",
    unit: '單位', unitHint: '全站速率的顯示單位，不影響統計和排名', hour: '小時', minute: '分鐘',
    exp: '經驗', mesos: '楓幣', model: '文字辨識模型', standard: '標準模型', lightweight: '輕量模型', standardBenefit: '辨識更準確', lightweightBenefit: '負載更低，辨識精度下降',
    modelHint: '建議使用標準模型。輕量模型可減少資源占用、降低裝置負載，但辨識精度較低，更容易出現誤讀或漏讀。', modelHintAction: '了解模型差異',
    recordingLocked: '請先結束目前記錄再切換模型', busyLocked: '請等待目前操作完成後再切換模型', switching: '正在切換模型…',
  },
  en: {
    experienceSettingsTitle: "EXP settings",
    rateUnitLabel: "Rate unit",
    rateUnitScope: "Syncs EXP, Mesos, charts and floating widgets across the site.",
    etaModeLabel: "Level-up ETA",
    realtime: "Realtime",
    average: "Average",
    etaScope: "Syncs the dashboard and floating widgets.",
    immediate: "Applies instantly",
    done: "Done",
    etaHelp: "Realtime follows recent rates and falls back to the average when unavailable. Average uses the current valid tracking period.",
    helpLabel: "About {setting}",
    etaRealtime: "Estimated using the realtime rate",
    etaAverage: "Estimated using the average rate",
    etaFallback: "Realtime rate unavailable; estimated using the average rate",
    etaUnavailable: "Not enough data to estimate yet",
    trendDescription: "Last {window}s: {p0} {unit}\nSession average: {p1} {unit}\nDifference: {p2} {unit}\nLast sample: {p3}\nArrows appear at 4% and hide 1.5s after returning to 2%, with recognition-delay protection. Window edges and cross-level gains may be estimated.",
    unit: 'Units', unitHint: 'Display units for rates across the site; statistics and rankings stay the same', hour: 'Hour', minute: 'Minute',
    exp: 'EXP', mesos: 'Mesos', model: 'Text recognition model', standard: 'Standard model', lightweight: 'Lightweight model', standardBenefit: 'More accurate', lightweightBenefit: 'Lower load, reduced accuracy',
    modelHint: 'Standard is recommended. Lightweight uses fewer resources and reduces device load, but is less accurate and more likely to misread or miss text.', modelHintAction: 'About the model options',
    recordingLocked: 'End the current record before switching models', busyLocked: 'Wait for the current operation to finish before switching models', switching: 'Switching model…',
  },
  ja: {
    experienceSettingsTitle: "経験値の設定",
    rateUnitLabel: "表示単位",
    rateUnitScope: "経験値・メル・グラフ・フローティング表示に共通です。",
    etaModeLabel: "レベル予測",
    realtime: "リアルタイム",
    average: "平均",
    etaScope: "ダッシュボードとフローティング表示に適用。",
    immediate: "すぐに反映",
    done: "完了",
    etaHelp: "リアルタイムは直近の効率を使用し、データ不足時は平均に切り替わります。平均は現在の有効な集計区間が対象です。",
    helpLabel: "{setting}について",
    etaRealtime: "リアルタイムの効率で予測",
    etaAverage: "平均効率で予測",
    etaFallback: "リアルタイムの効率を取得できないため、平均効率で予測しています",
    etaUnavailable: "データ不足のため、まだ予測できません",
    trendDescription: "過去 {window} 秒: {p0} {unit}\nセッション平均: {p1} {unit}\n差分: {p2} {unit}\n最後のサンプル: {p3}\n認識遅延保護機能により、矢印は 4% で表示され、2% に戻ってから 1.5 秒後に非表示になります。ウィンドウのエッジとクロスレベルのゲインを推定できます。",
    unit: '単位', unitHint: 'サイト全体の効率表示の単位です。統計や順位には影響しません', hour: '時間', minute: '分',
    exp: '経験値', mesos: 'メル', model: '文字認識モデル', standard: '標準モデル', lightweight: '軽量モデル', standardBenefit: '認識精度を優先', lightweightBenefit: '負荷を軽減、認識精度は低下',
    modelHint: '標準モデルを推奨します。軽量モデルはリソース使用量と端末の負荷を抑えますが、認識精度が低く、文字の読み間違いや見落としが起きやすくなります。', modelHintAction: 'モデルの違いを見る',
    recordingLocked: 'モデルを切り替える前に記録を終了してください', busyLocked: '現在の処理が完了するまでお待ちください', switching: 'モデルを切り替え中…',
  },
  ko: {
    experienceSettingsTitle: "경험치 설정",
    rateUnitLabel: "표시 단위",
    rateUnitScope: "사이트 전체의 경험치, 메소, 차트 및 플로팅 창에 적용됩니다.",
    etaModeLabel: "레벨업 예상",
    realtime: "실시간 속도",
    average: "평균 속도",
    etaScope: "대시보드와 플로팅 창에 적용됩니다.",
    immediate: "즉시 적용",
    done: "완료",
    etaHelp: "실시간은 최근 효율을 따르며, 데이터가 부족하면 평균을 사용합니다. 평균은 현재 유효한 집계 구간을 기준으로 합니다.",
    helpLabel: "{setting} 도움말",
    etaRealtime: "실시간 속도로 계산",
    etaAverage: "평균 속도로 계산",
    etaFallback: "실시간 속도를 사용할 수 없어 평균 속도로 계산 중입니다",
    etaUnavailable: "데이터가 부족하여 아직 계산할 수 없습니다",
    trendDescription: "지난 {window}초: {p0} {unit}\n세션 평균: {p1} {unit}\n차이: {p2} {unit}\n마지막 샘플: {p3}\n화살표는 4%에서 나타나고 인식 지연 보호 기능을 통해 2%로 돌아온 뒤 1.5초 후에 숨겨집니다. 창 가장자리와 교차 수준 이득이 추정될 수 있습니다.",
    unit: '단위', unitHint: '사이트 전체 효율의 표시 단위이며 통계와 순위에는 영향을 주지 않습니다', hour: '시간', minute: '분',
    exp: '경험치', mesos: '메소', model: '문자 인식 모델', standard: '표준 모델', lightweight: '경량 모델', standardBenefit: '더 정확한 인식', lightweightBenefit: '부하 감소, 인식 정확도 저하',
    modelHint: '표준 모델을 권장합니다. 경량 모델은 리소스 사용량과 기기 부하를 줄이지만 인식 정확도가 낮아 문자를 잘못 읽거나 놓칠 가능성이 높습니다.', modelHintAction: '모델 차이 보기',
    recordingLocked: '모델을 변경하려면 현재 기록을 종료하세요', busyLocked: '현재 작업이 끝날 때까지 기다려 주세요', switching: '모델 변경 중…',
  },
} satisfies Record<AppLocale, Record<string, string>>

export function rateSuffix(locale: string, unit: RateUnit = 'hour'): string {
  const language = normalizeLocale(locale)
  return `/${language === 'en' ? unit === 'minute' ? 'min' : 'h' : displaySettingsMessages[language][unit]}`
}

export function rateLabel(locale: string, unit: RateUnit = 'hour', metric: 'exp' | 'mesos' = 'exp'): string {
  return `${displaySettingsMessages[normalizeLocale(locale)][metric]} / ${rateSuffix(locale, unit).slice(1)}`
}
